<script setup>
// =============================================================================
// FangatePriceModal — edit price OR create a new sales link
//
// Two modes:
//   • 'change' → PATCH /api/products/{activeVariantId}/price
//                Updates the price of the active variant. Slug stays.
//   • 'new'    → POST  /api/products/{sourceId}/price-links
//                Fangate CLONES the product into a new product record with
//                its own short link + own price. We emit the new variant
//                back so the parent can absorb it under the same card.
//
// Self-contained: owns its own form state + validation + saves. Parent just
// wires open/close, supplies the product + active variant, and listens to
// the saved-* events to update its productLinks model.
//
// Price range — Fangate platform constants:
//   min = €5  (`500` cents — also enforced server-side on POST, per docs)
//   max = €224 (client-only cap per ops request; not in public docs)
//
// Sync-API mode: spinner on confirm, modal stays open on error and shows
// the upstream `errors_message`, modal closes on success.
// =============================================================================

import { reactive, computed, watch } from 'vue';
import FangateAPI from 'dashboard/api/fangate';

const props = defineProps({
  open:           { type: Boolean, default: false },
  // The product the card represents (for title fallback on display).
  product:        { type: Object,  default: null },
  // The variant currently active on the card — { id, title, price, url }.
  // For 'change' mode we PATCH this.id. For 'new' mode this.id is the
  // SOURCE that gets cloned.
  activeVariant:  { type: Object,  default: null },
  // Demo flag — when false we don't hit the API, only update local state.
  isConnected:    { type: Boolean, default: false },
  // Locale-aware money formatter (parent provides — same instance as cards).
  formatBalance:  { type: Function, default: n => String(n) },
  // Standalone currency symbol for the input prefix (e.g. "€", "$").
  currencySymbol: { type: String,  default: '€' },
});

const emit = defineEmits([
  'update:open',
  // Payload: server-side updated object — { id, link, price }. Price is in
  // EUROS (modal converts from the cents Fangate returns).
  'saved-change-price',
  // Payload: (sourceVariantId, newVariant{ id, title, price, url }).
  'saved-new-link',
]);

const FANGATE_MIN_PRICE = 5;
const FANGATE_MAX_PRICE = 224;

const state = reactive({
  mode:      'change',   // 'change' | 'new'
  price:     '',
  linkTitle: '',
  linkDesc:  '',
  saving:    false,
  error:     '',
});

// When the modal opens, seed the form from the active variant. Keeps the
// modal "stateless" between opens — closing leaves stale values that the
// next open will overwrite.
watch(() => props.open, isOpen => {
  if (!isOpen) return;
  state.mode      = 'change';
  state.price     = String(props.activeVariant?.price ?? '');
  state.linkTitle = props.activeVariant?.title ?? props.product?.title ?? '';
  state.linkDesc  = '';
  state.error     = '';
});

const priceInvalid = computed(() => {
  const raw = String(state.price || '').trim();
  if (raw === '') return true;
  const n = parseFloat(raw);
  if (isNaN(n)) return true;
  return n < FANGATE_MIN_PRICE || n > FANGATE_MAX_PRICE;
});

const close = () => {
  emit('update:open', false);
  // Strip focus from any element that retained it across the modal lifecycle
  // (otherwise some browsers render a focus ring on the trigger button that
  // can read as a blinking caret).
  if (typeof document !== 'undefined') document.activeElement?.blur?.();
};

const setMode = m => { state.mode = m; state.error = ''; };

const confirm = async () => {
  if (!props.product || !props.activeVariant) return;

  const raw   = String(state.price || '').trim();
  const price = parseFloat(raw);
  if (raw === '' || isNaN(price)) {
    state.error = `Enter a price between ${props.formatBalance(FANGATE_MIN_PRICE)} and ${props.formatBalance(FANGATE_MAX_PRICE)}.`;
    return;
  }
  if (price < FANGATE_MIN_PRICE || price > FANGATE_MAX_PRICE) {
    state.error = `Price must be between ${props.formatBalance(FANGATE_MIN_PRICE)} and ${props.formatBalance(FANGATE_MAX_PRICE)}.`;
    return;
  }

  state.error  = '';
  state.saving = true;
  const priceCents = Math.round(price * 100);

  try {
    if (state.mode === 'change') {
      if (!props.activeVariant.id) throw new Error('Internal: variant has no Fangate id.');
      let updated;
      if (props.isConnected) {
        const resp = await FangateAPI.updateProductPrice(props.activeVariant.id, priceCents);
        updated = {
          ...props.activeVariant,
          price: typeof resp?.price === 'number' ? resp.price / 100 : price,
          url:   resp?.link || props.activeVariant.url,
        };
      } else {
        // Demo mode — local-only update.
        updated = { ...props.activeVariant, price };
      }
      emit('saved-change-price', updated);
    } else {
      if (!props.activeVariant.id) throw new Error('Internal: source variant has no Fangate id.');
      let newVariant;
      if (props.isConnected) {
        const created = await FangateAPI.createPriceLink(props.activeVariant.id, {
          price:             priceCents,
          title:             state.linkTitle || undefined,
          publicDescription: state.linkDesc  || undefined,
        });
        newVariant = {
          id:    created?.id,
          title: created?.title || state.linkTitle || props.product?.title,
          price: typeof created?.price === 'number' ? created.price / 100 : price,
          url:   created?.link || FangateAPI.buildProductLink(created?.id),
        };
      } else {
        // Demo mode — synthesize a fake id so the L-pill still works locally.
        const fakeId = 'demo-' + Date.now();
        newVariant = {
          id:    fakeId,
          title: state.linkTitle || props.product?.title,
          price,
          url:   FangateAPI.buildProductLink(fakeId),
        };
      }
      emit('saved-new-link', props.activeVariant.id, newVariant);
    }
    close();
  } catch (e) {
    // Modal stays open so the user can fix and retry.
    state.error = e?.message || 'Save failed.';
  } finally {
    state.saving = false;
  }
};
</script>

<template>
  <div v-if="open" class="fp-modal-overlay" @click.self="close">
    <div class="fp-modal" @click.stop>
      <div class="fp-modal-head">
        <div>
          <div class="fp-modal-eyebrow">Manage price</div>
          <div class="fp-modal-title">{{ product?.title }}</div>
        </div>
        <button class="fp-modal-close" @click="close">×</button>
      </div>

      <div class="fp-modal-tabs">
        <button class="fp-modal-tab"
                :class="{ active: state.mode === 'change' }"
                @click="setMode('change')">
          Change price
        </button>
        <button class="fp-modal-tab"
                :class="{ active: state.mode === 'new' }"
                @click="setMode('new')">
          + New link with new price
        </button>
      </div>

      <div class="fp-modal-body">
        <div v-if="state.mode === 'new'" class="fp-modal-note">
          Creates a new sales link for the same content. The original link
          keeps its current price. Each link has its own price + analytics.
        </div>

        <label class="fp-modal-label">New price</label>
        <div class="fp-modal-price-wrap">
          <span class="fp-modal-price-prefix">{{ currencySymbol }}</span>
          <input v-model="state.price" type="number"
                 :min="FANGATE_MIN_PRICE" :max="FANGATE_MAX_PRICE" step="0.01"
                 :placeholder="String(FANGATE_MIN_PRICE) + '.00'"
                 class="fp-modal-price-input" />
        </div>
        <div class="fp-modal-hint">
          Min price: {{ formatBalance(FANGATE_MIN_PRICE) }} — Max price: {{ formatBalance(FANGATE_MAX_PRICE) }}.
        </div>

        <template v-if="state.mode === 'new'">
          <div class="fp-modal-grid">
            <div>
              <label class="fp-modal-label">Link label / title</label>
              <input v-model="state.linkTitle" type="text"
                     :placeholder="product?.title"
                     class="fp-modal-text-input" />
            </div>
            <div>
              <label class="fp-modal-label">Public description</label>
              <input v-model="state.linkDesc" type="text"
                     placeholder="Optional description"
                     class="fp-modal-text-input" />
            </div>
          </div>
        </template>

        <div v-if="state.error" class="fp-modal-err">{{ state.error }}</div>
      </div>

      <div class="fp-modal-foot">
        <button class="fp-modal-cancel" @click="close">Cancel</button>
        <button class="fp-modal-confirm"
                :disabled="state.saving || priceInvalid"
                @click="confirm">
          {{ state.saving
               ? 'Saving…'
               : state.mode === 'change' ? 'Save price' : 'Create link' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.fp-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(11, 20, 20, 0.35);
  display: flex; align-items: center; justify-content: center;
  // Higher than FangateItemEditModal (9999) so the Edit-price popup stacks
  // ABOVE the item-edit dialog when both are open simultaneously (price was
  // opening behind the item editor because the later DOM sibling wins ties).
  z-index: 10001; padding: 20px;
}
.fp-modal {
  background: var(--bg-panel);
  border-radius: 13px;
  width: 100%; max-width: 460px;
  box-shadow: 0 16px 48px rgba(11, 20, 20, 0.2);
  overflow: hidden;
}
.fp-modal-head {
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-soft);
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px;
}
.fp-modal-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--ink-muted); margin-bottom: 3px;
}
.fp-modal-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 700;
  color: var(--ink-strong);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 300px;
}
.fp-modal-close {
  width: 26px; height: 26px; border-radius: 7px;
  border: none; background: transparent;
  color: var(--ink-muted); cursor: pointer;
  font-size: 18px; flex-shrink: 0;
  &:hover { background: var(--bg-subtle); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
}
.fp-modal-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-soft);
  background: var(--bg-subtle);
}
.fp-modal-tab {
  flex: 1; padding: 9px;
  border: none; border-bottom: 2px solid transparent;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  color: var(--ink-muted); cursor: pointer;
  &.active {
    border-bottom-color: rgba(31, 42, 42, 0.85);
    color: var(--ink-strong);
  }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: -2px; }
}
.fp-modal-body { padding: 18px 20px; display: flex; flex-direction: column; gap: 10px; }
.fp-modal-label {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-muted); margin-bottom: 6px;
}
.fp-modal-note {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: var(--ink-muted);
  line-height: 1.5;
}
// Single unified field: € prefix + input share the same background and
// border. No internal divider — looks like one input, not two stuck cells.
// Fixed 38px height with content vertically centered. Matches the look of
// .fp-modal-text-input below (title / description fields in the same modal).
.fp-modal-price-wrap {
  display: flex; align-items: stretch;
  height: 38px; box-sizing: border-box;
  border: 1px solid var(--border-soft);
  border-radius: 8px; overflow: hidden;
  background: var(--bg-subtle);
  transition: border-color 0.12s;
  &:focus-within { border-color: rgba(31, 42, 42, 0.4); }
}
.fp-modal-price-prefix {
  display: flex; align-items: center; justify-content: center;
  padding: 0 10px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  color: var(--ink-muted);
  flex-shrink: 0;
}
.fp-modal-price-input {
  flex: 1; min-width: 0;
  // Explicit height matching the wrap's internal content area (38px - 2x1px
  // border = 36px). Combined with line-height: 36px this centers the digit
  // glyphs vertically — without it, browsers top-anchor input text.
  height: 36px; line-height: 36px;
  box-sizing: border-box;
  border: none; background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600;
  color: var(--ink-strong);
  padding: 0 10px 0 0; outline: none;
  // Hide Webkit + Firefox up/down spinner arrows — they crowd the field
  // and the keyboard's step value already exposes the same affordance.
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
.fp-modal-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px; color: var(--ink-muted);
  margin-top: -2px;
}
.fp-modal-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  margin-top: 4px;
}
// Mobile (≤768px): side-by-side fields get cramped → stack them.
@media (max-width: 768px) {
  .fp-modal-grid { grid-template-columns: 1fr; }
}
.fp-modal-text-input {
  width: 100%; box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; padding: 8px 10px;
  border-radius: 8px; border: 1px solid var(--border-soft);
  background: var(--bg-subtle); outline: none;
  color: var(--ink-strong);
  &:focus { border-color: rgba(31, 42, 42, 0.4); }
}
.fp-modal-err {
  padding: 8px 12px; border-radius: 8px;
  background: #fee2e2; color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}
.fp-modal-foot {
  padding: 12px 20px;
  border-top: 1px solid var(--border-soft);
  background: var(--bg-subtle);
  display: flex; justify-content: flex-end; gap: 8px;
}
.fp-modal-cancel {
  padding: 7px 14px; border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
}
.fp-modal-confirm {
  padding: 7px 16px; border-radius: 7px;
  border: none;
  background: rgba(31, 42, 42, 0.85); color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--accent-ring); outline-offset: 2px; }
}
</style>
